
/*!
 * Copyright (C) 2024, Peppermill Inc.
 * All rights reserved.
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *//*
 * @version 1.0.1
 * @author Jasper Howard <jhoward@peppermillcas.com>
 * @changelog
 *		2024-05-14 (1.0.0)
 *			- Add <show-overlay-dialog> component to facilitate displaying dialogs.
 *
 *		2024-06-17 (1.0.1) (jhoward)
 *			- Fix <show-overlay-dialog> callbacks with form elements.
 */

import {defineComponent} from 'vue';
import PeppermillDialog, {PeppermillDialogComponent} from "./PeppermillDialog.vue";

export interface OverlayDialogOptions {
  alertMode?: boolean;
  confirmAccept?: string;
  confirmDeny?: string;
  confirmTitle?: string;
  confirmBody?: string;
  callback?: string;
  callbackDeny?: string;
  query?: string;
  forceCallback?: boolean;
  forceCallbackTimeout?: number;
  forceCallbackTimer?: number;
  isWideUi?: boolean;
  isOverlayHidden?: boolean;
}

interface DialogFetchResponse {
  STATUS: 'SUCCESS' | 'ERROR';
  MSG: string;
}

export default defineComponent({
  name: 'ShowOverlayDialog',
  components: {PeppermillDialog},
  mounted() {
    window.ajaxLinkRegisterCallback = (action: string, callback: (...args: unknown[]) => void) => {
      this.callbacks[action] = callback;
    };

    window.showOverlayDialog = (linkData: OverlayDialogOptions, linkEle?: HTMLElement) => {
      if (!linkEle || linkEle.dataset.locked !== 'YES') {
        this.dialogOptions = linkData;
        this.dialogLinkElement = linkEle || null;
      }
    };
  },
  data() {
    return {
      callbacks: {} as { [key: string]: Function },
      isActive: false,
      dialogOptions: null as OverlayDialogOptions | null,
      dialogLinkElement: null as HTMLElement | null,
      defaults: {
        alertMode: false,
        confirmAccept: 'OK',
        confirmDeny: 'CANCEL',
        confirmTitle: '',
        confirmBody: '',
        callback: '',
        callbackDeny: '',
        query: '',
        forceCallback: false,
        forceCallbackTimeout: 10000,
        forceCallbackTimer: -1,
        isWideUi: false,
        isOverlayHidden: false,
        dataObj: {}
      },
    }
  },
  watch: {
    isActive() {
      if (!this.isActive) {
        setTimeout(() => {
          if (this.dialogLinkElement) {
            this.dialogLinkElement.dataset.locked = 'NO';
          }

          this.dialogOptions = null;
          this.dialogLinkElement = null;
        }, 256);
      }
    },
    dialogOptions() {
      if (!!this.dialogOptions) {
        setTimeout(() => {
          this.isActive = true;

          if (this.dialogLinkElement) {
            this.dialogLinkElement.dataset.locked = 'YES';
          }

          if (this.dialogOptions?.forceCallback) {
            this.dialogOptions.forceCallbackTimer = window.setTimeout(() => {
              this.handleCallback((this.$refs.dialog as PeppermillDialogComponent).getDialogFormData());
            }, this.dialogOptions.forceCallbackTimeout);
          }
        }, 32);
      }
    }
  },
  computed: {
    dialogBody(): string {
      if (this.dialogOptions) {
        const bodyStart = (this.dialogOptions.confirmBody?.substring(0, 3) === '<p>') ? '' : '<p>';
        const bodyEnd = (this.dialogOptions.confirmBody?.substring(this.dialogOptions.confirmBody.length - 4, 4) === '</p>') ? '' : '</p>';

        return bodyStart + this.dialogOptions.confirmBody + bodyEnd;
      }

      return '';
    },
    options() {
      return {...this.defaults, ...(this.dialogOptions || {})};
    },
  },
  methods: {
    handleCallback(formData: FormData | null) {
      if (this.dialogOptions?.callback && this.callbacks[this.options.callback]) {
        this.callbacks[this.options.callback].apply(this.dialogLinkElement || window, Array.from(formData?.values() || []));
        this.isActive = false;
      } else if (this.dialogOptions?.query) {
        this.sendOverlayDialogFetch(formData);
      } else {
        this.isActive = false;
      }
    },
    handleCallbackDeny() {
      if (this.dialogOptions?.callbackDeny && this.callbacks[this.options.callbackDeny]) {
        if (this.dialogLinkElement) {
          this.callbacks[this.options.callbackDeny].apply(this.dialogLinkElement, [this.dialogOptions]);
        } else {
          this.callbacks[this.options.callbackDeny].apply(window, [this.dialogOptions]);
        }
      }
      this.isActive = false;
    },

    async sendOverlayDialogFetch(formData: FormData | null): Promise<void> {
      if (this.dialogOptions?.query) {
        window.showLoader();
        try {
          const response = await fetch('/library/api/ajax-links.php?' + this.dialogOptions.query, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json',
            },
            body: (formData) ? JSON.stringify(Object.fromEntries(formData)) : '',
          });
          const json = await response.json();
          if (json.STATUS && json.STATUS === 'SUCCESS') {
            window.pepNotify.show(10000, json.MSG);
          } else {
            window.pepNotify.show(10000, json.MSG, 'error_outline', 'ERROR');
          }
        } catch(e) {
          const error = e as Error;
          console.log(error);
          window.pepNotify.show(10000, error.message, 'error_outline', 'ERROR');
        } finally {
          window.hideLoader();
          this.isActive = false;
        }
      }
    }
  }
})
